import { cardLabel, rankOf, type Card } from '../cards';
import { STEP_BASIS, luckOf, luckTarget, secondChanceFrame } from './luck';
import type { CasinoGame, TableOption } from './types';

/**
 * High-Low: a card is showing; guess whether the next is higher or lower.
 * A right guess multiplies your stake by 1 / P(right) — safe guesses pay
 * little, long shots a lot — and the house's EDGE is taken once, on the
 * first guess ((1 - EDGE) / P(right)), so cashing out after any number of
 * guesses returns (1 - EDGE) on average. (Balance patch, 2026-09-23, user:
 * the edge used to be taken on every guess and compounded, so a long
 * streak was a trap — two guesses ~96%, five ~90%.) A tie (same rank) loses. Cash out any time; one
 * wrong guess loses the lot. Cards are drawn from an endless shoe (every
 * rank equally likely every time), so the odds only depend on the card
 * showing. Aces are high.
 */
export const EDGE = 0.02;
/** A streak this long wins the floor jackpot. */
export const JACKPOT_STREAK = 10;

/** 0 (two) .. 12 (ace). */
export function hiloRank(card: Card): number {
  return (rankOf(card) + 12) % 13;
}

export function chance(card: Card, guess: 'higher' | 'lower'): number {
  const r = hiloRank(card);
  return (guess === 'higher' ? 12 - r : r) / 13;
}

export function guessMultiplier(card: Card, guess: 'higher' | 'lower', edge: number): number {
  const p = chance(card, guess);
  return p > 0 ? (1 - edge) / p : 0;
}

/** The edge a guess carries: all of it on the first guess, none after. */
export const guessEdge = (state: { streak: number; edge: number }) => (state.streak === 0 ? state.edge : 0);

export interface HighLowState {
  phase: 'idle' | 'streak' | 'done';
  card: Card;
  /** Current stake multiple (bet x this = cash-out value). */
  value: number;
  bet: number;
  streak: number;
  edge: number;
  /** Cards drawn this streak, oldest first (for the lens). */
  trail: Card[];
  returned: number;
  lost: boolean;
  /** The table's luck when the streak started (★ luck redraws a wrong card). */
  luck?: number;
  /** A wrong card was redrawn and came up right on the last guess. */
  lucky?: boolean;
}

function cashValue(state: HighLowState): number {
  return Math.round(state.bet * state.value);
}

export const highLow: CasinoGame<HighLowState> = {
  id: 'highlow',
  usesLuck: true,
  name: 'High-Low',
  blurb: 'Guess if the next card is higher or lower. Cash out any time.',
  baseMinBet: 5,
  baseMaxBet: 100,
  specialPerk: 'Luck: a wrong card is sometimes drawn again.',
  jackpotRule: `${JACKPOT_STREAK} right guesses in a row.`,
  lockedHint: '(cash out to leave)',
  create: () => ({ phase: 'idle', card: 0, value: 1, bet: 0, streak: 0, edge: EDGE, trail: [], returned: 0, lost: false }),
  locked: (state) => state.phase === 'streak',
  options(state): TableOption[] {
    if (state.phase !== 'streak') return [{ id: 'deal', label: state.phase === 'done' ? 'Play again' : 'Deal' }];
    const opts: TableOption[] = [];
    for (const guess of ['higher', 'lower'] as const) {
      const m = guessMultiplier(state.card, guess, guessEdge(state));
      if (m > 0) opts.push({ id: guess, label: `${guess === 'higher' ? 'Higher' : 'Lower'} x${m.toFixed(2)}` });
    }
    opts.push({ id: 'cashout', label: state.streak === 0 ? 'Take bet back' : `Cash out $${cashValue(state)}` });
    return opts;
  },
  describe(state, _env, highlighted) {
    if (state.phase === 'idle') return 'Deal a card, then call the next one. Same rank loses.';
    // The trail only earns its line once there's more than the card showing.
    const trail = state.trail.length > 1 ? state.trail.slice(-5).map(cardLabel).join(' ') : '';
    if (state.phase === 'done') {
      const net = state.returned - state.bet;
      const result = state.lost ? `Wrong. -$${state.bet}` : net > 0 ? `Cashed out +$${net}` : 'Took the bet back.';
      return `${trail}\n\n${result}\nStreak: ${state.streak}`;
    }
    const guess = highlighted === 'higher' || highlighted === 'lower' ? highlighted : null;
    const odds = guess ? `\nChance: ${Math.round(chance(state.card, guess) * 100)}%` : '';
    // A ★ marks a card the table's luck drew again for you.
    const lines = [`Showing: ${cardLabel(state.card)}${state.lucky ? ' ★' : ''}`];
    if (trail) lines.push(trail);
    lines.push('', `Streak ${state.streak} · worth $${cashValue(state)}${odds}`);
    return lines.join('\n');
  },
  act(state, optionId, env) {
    if (state.phase !== 'streak') {
      if (optionId !== 'deal') return { state, bankroll: env.bankroll };
      const card = env.rng.int(52);
      return {
        state: { phase: 'streak', card, value: 1, bet: env.bet, streak: 0, edge: EDGE, luck: luckOf(env), trail: [card], returned: 0, lost: false },
        bankroll: env.bankroll - env.bet,
      };
    }
    if (optionId === 'cashout') {
      const returned = cashValue(state);
      return {
        state: { ...state, phase: 'done', returned },
        bankroll: env.bankroll + returned,
        // Taking the bet back before a guess risked nothing, so it isn't a
        // play: no drink bonuses, restocks or jackpot growth (found in a
        // Codex review, 2026-09-27: deal and take back paid Espresso +10%).
        settled: state.streak === 0 ? undefined : { wagered: state.bet, returned },
      };
    }
    if (optionId !== 'higher' && optionId !== 'lower') return { state, bankroll: env.bankroll };
    const multiplier = guessMultiplier(state.card, optionId, guessEdge(state));
    if (multiplier === 0) return { state, bankroll: env.bankroll };
    const guess = optionId;
    const isRight = (card: number) => (guess === 'higher' ? hiloRank(card) > hiloRank(state.card) : hiloRank(card) < hiloRank(state.card));
    let next = env.rng.int(52);
    // The ★'s luck: a wrong card is sometimes redrawn. Aimed so each guess
    // is worth LUCK_RTP^(1/STEP_BASIS) (the first also makes up the edge):
    // the chance c becomes c * ratio, and a redraw is right with chance c,
    // so the redraw rate is (c*ratio - c) / ((1-c) * c).
    let lucky = false;
    let wrong: number | null = null;
    const c = chance(state.card, guess);
    const ratio = luckTarget(state.luck ?? 0) ** (1 / STEP_BASIS) / (1 - guessEdge(state));
    // No luck, no redraws (a normal table): the ratio alone would still come
    // out above 1, since it aims for 100% from a 98% guess.
    const redraw = (state.luck ?? 0) > 0 && ratio > 1 && c > 0 && c < 1 ? Math.min(1, (c * ratio - c) / ((1 - c) * c)) : 0;
    if (!isRight(next) && env.rng.next() < redraw) {
      const lost = next;
      next = env.rng.int(52);
      lucky = isRight(next);
      if (lucky) wrong = lost;
    }
    // The next card lands face down for a beat; a ★ second chance shows the
    // wrong card first, then draws again.
    const facing = `Showing: ${cardLabel(state.card)}\nNext: ??\n\nYou said ${guess}...`;
    const frames = wrong === null ? [facing] : [facing, secondChanceFrame(`Showing: ${cardLabel(state.card)}\nNext: ${cardLabel(wrong)}`), facing];
    const trail = [...state.trail, next];
    const right = isRight(next);
    if (!right) {
      return {
        state: { ...state, phase: 'done', card: next, trail, returned: 0, lost: true },
        bankroll: env.bankroll,
        settled: { wagered: state.bet, returned: 0 },
        frames,
      };
    }
    const streak = state.streak + 1;
    return {
      state: { ...state, card: next, trail, value: state.value * multiplier, streak, lucky },
      bankroll: env.bankroll,
      frames,
      jackpot: streak === JACKPOT_STREAK,
    };
  },
};
